import { CircleAlert, CircleCheck } from 'lucide-react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMetric, formatNumber } from '../../ui/format';
import { useMetricLabel } from './DeviceLive';
import { editable, editSpec, REPORT_LIST_MAX, reportable, same, validate } from './readEdit';
import registry from './readFields.json';

/**
 * Semantic view of a decoded read answer: a read-only configuration/status form. Each field is shown with the control
 * that matches what it represents (switch, select, percentage bar, time, date/time, duration, from/to, bit list,
 * alarm badges), grouped in sections; composite structures get their own card (light-sensor rule, segments, position,
 * server, tasks, report list). Semantics come from readFields.json (generated from the vendor documents by
 * scripts/gen-read-fields.mjs). A field without documented semantics is shown raw and marked; nothing is inferred.
 * The raw answer stays available, collapsed.
 *
 * With an `edit` state (device forms, readEdit.ts) the fields that may be set on this device's protocol become live
 * controls bound to a draft; every other field keeps its read-only control.
 */

type Kind =
  | 'bool'
  | 'alarm'
  | 'enum'
  | 'percent'
  | 'number'
  | 'duration'
  | 'time'
  | 'datetime'
  | 'text'
  | 'id'
  | 'features'
  | 'alarms'
  | 'flags'
  | 'list'
  | 'group'
  | 'weekdays'
  | 'undocumented';
type Spec = {
  kind?: Kind;
  section?: string;
  unit?: string;
  min?: number;
  max?: number;
  special?: number[];
  options?: (string | number)[];
  names?: string[];
  perChannel?: boolean;
  format?: string;
  zone?: string;
  note?: string;
};
type Json = null | boolean | number | string | Json[] | { [k: string]: Json };
type Obj = { [k: string]: Json };

const FIELDS = registry.fields as Record<string, Spec>;
const SECTIONS = registry.sections;
const LIST_CONTEXT = registry.listContext;
const isObj = (v: Json | undefined): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isBits = (v: Json): v is Obj & { bits: Obj } => isObj(v) && isObj(v.bits);
const specOf = (k: string): Spec | undefined => FIELDS[k];

/** Draft of the device form: edited values by field (same shapes as the read answer). */
export type EditState = {
  protocol: string | undefined;
  /** configuration capabilities the caller may send to this device */
  writes: ReadonlySet<string>;
  draft: Obj;
  set: (key: string, value: Json) => void;
};
const EditCtx = createContext<EditState | null>(null);
/** The edit state when `key` (current value `v`) may be edited, else null. */
function useEditable(key: string, v: Json): EditState | null {
  const e = useContext(EditCtx);
  return e && editable(key, v, e.protocol, e.writes) ? e : null;
}

/* ---------- small read-only controls ---------- */

function Switch({ on }: { on: boolean }) {
  const { t } = useTranslation();
  return (
    <span className={`ro-switch${on ? ' is-on' : ''}`} role="switch" aria-checked={on} aria-readonly>
      <span className="ro-switch-track" aria-hidden>
        <span className="ro-switch-thumb" />
      </span>
      {on ? t('devices.reads.view.yes') : t('devices.reads.view.no')}
    </span>
  );
}

function AlarmBadge({ active, label }: { active: boolean; label?: string }) {
  const { t } = useTranslation();
  return (
    <span className={`ro-alarm${active ? ' is-active' : ''}`}>
      {active ? <CircleAlert size={13} /> : <CircleCheck size={13} />}
      {label ?? (active ? t('devices.reads.view.active') : t('devices.reads.view.inactive'))}
    </span>
  );
}

function Percent({ v }: { v: number }) {
  return (
    <span className="ro-percent">
      <span className="ro-percent-bar" aria-hidden>
        <span style={{ width: `${Math.max(0, Math.min(100, v))}%` }} />
      </span>
      {formatNumber(v)} %
    </span>
  );
}

/** A value shown as received and marked: no documented meaning, a special raw value, or outside the documented range. */
function Raw({ v, why }: { v: Json; why: 'undocumented' | 'special' | 'range' }) {
  const { t } = useTranslation();
  const shown =
    typeof v === 'string'
      ? v || '""'
      : typeof v === 'number' && why === 'special'
        ? `${v} (0x${v.toString(16).toUpperCase()})`
        : JSON.stringify(v);
  return (
    <span className="ro-raw">
      <code>{shown}</code>
      <span className="ro-tag" title={t(`devices.reads.view.why.${why}`)}>
        {t(`devices.reads.view.tag.${why}`)}
      </span>
    </span>
  );
}

const hhmm = (v: string) => {
  const m = /^(\d\d):?(\d\d)$/.exec(v);
  return m && Number(m[1]) <= 23 && Number(m[2]) <= 59 ? `${m[1]}:${m[2]}` : null;
};

function TimeBox({ v }: { v: string }) {
  const t = hhmm(v);
  if (!t) return <Raw v={v} why="undocumented" />;
  // a text box, not type="time": the browser would render it in its own 12/24 h format
  return <input className="input ro-input ro-time" value={t} readOnly tabIndex={-1} aria-label={t} />;
}

function DateTimeBox({ v, spec }: { v: string; spec: Spec }) {
  const { t, i18n } = useTranslation();
  let parts: number[] | null = null;
  if (spec.format === 'ymd-comma') {
    const m = /^(\d{4})\/(\d{1,2})\/(\d{1,2}),(\d{1,2}):(\d{1,2}):(\d{1,2})$/.exec(v);
    if (m) parts = m.slice(1).map(Number);
  } else {
    const m = /^(\d{4})-(\d\d)-(\d\d)T(\d\d):(\d\d)(?::(\d\d))?/.exec(v);
    if (m) parts = m.slice(1).map((x) => Number(x ?? 0));
  }
  if (!parts) return <Raw v={v} why="undocumented" />;
  const [y, mo, d, h, mi, s] = parts as [number, number, number, number, number, number];
  // The device's own clock reading, shown as written (no conversion): its zone is the device's.
  const text = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'medium',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, mo - 1, d, h, mi, s)));
  return (
    <span className="ro-datetime">
      {text}
      <span className="muted small">
        {' · '}
        {t(spec.zone === 'gmt' ? 'devices.reads.view.zoneGmt' : 'devices.reads.view.zoneDevice')}
      </span>
    </span>
  );
}

function useLabel() {
  const { t } = useTranslation();
  return (key: string) => {
    const human = key
      .replace(/_(lux|dbm|db|pct|min|ma|va|var|wh|hz|deg|c|v|a|w|s|bytes)$/, '')
      .replace(/[_.]/g, ' ');
    return t(`devices.reads.field.${key}` as 'devices.reads.field.version', {
      defaultValue: human.charAt(0).toUpperCase() + human.slice(1),
    });
  };
}

function useDuration() {
  const { t } = useTranslation();
  return (s: number) => {
    const parts: [number, 'd' | 'h' | 'm' | 's'][] = [
      [Math.floor(s / 86400), 'd'],
      [Math.floor((s % 86400) / 3600), 'h'],
      [Math.floor((s % 3600) / 60), 'm'],
      [Math.floor(s % 60), 's'],
    ];
    const used = parts.filter(([n]) => n > 0).slice(0, 2);
    if (!used.length) return t('devices.reads.view.duration.s', { n: 0 });
    return used
      .map(([n, u]) => t(`devices.reads.view.duration.${u}` as 'devices.reads.view.duration.s', { n }))
      .join(' ');
  };
}

/* ---------- one value with its control ---------- */

function Control({ k, v, spec }: { k: string; v: Json; spec?: Spec }): ReactNode {
  const { t } = useTranslation();
  const label = useLabel();
  const duration = useDuration();
  const kind = spec?.kind;
  if (v === null || v === '') return <span className="muted">—</span>;
  if (!spec || kind === 'undocumented') return <Raw v={v} why="undocumented" />;
  if (Array.isArray(v) && kind !== 'weekdays' && kind !== 'list') {
    if (v.length === 1) return <Control k={k} v={v[0]!} spec={spec} />;
    return (
      <span className="ro-channels">
        {v.map((x, i) => (
          <span key={i}>
            <span className="muted small">{t('devices.reads.view.channel', { n: i + 1 })}</span>{' '}
            <Control k={k} v={x} spec={spec} />
          </span>
        ))}
      </span>
    );
  }
  if (typeof v === 'number' && spec.special?.includes(v)) return <Raw v={v} why="special" />;
  if (
    typeof v === 'number' &&
    spec.min !== undefined &&
    spec.max !== undefined &&
    (v < spec.min || v > spec.max)
  )
    return <Raw v={v} why="range" />;
  switch (kind) {
    case 'bool':
      return typeof v === 'boolean' ? <Switch on={v} /> : <Raw v={v} why="undocumented" />;
    case 'alarm':
      return typeof v === 'boolean' ? <AlarmBadge active={v} /> : <Raw v={v} why="undocumented" />;
    case 'enum': {
      const i = spec.options?.findIndex((o) => String(o) === String(v)) ?? -1;
      if (i < 0) return <Raw v={v} why="undocumented" />;
      const name = (o: string | number, j: number) => String(spec.names?.[j] ?? o);
      return (
        <select className="input ro-input" value={String(v)} disabled aria-label={label(k)}>
          {spec.options!.map((o, j) => (
            <option key={String(o)} value={String(o)}>
              {t(`devices.reads.enum.${name(o, j)}` as 'devices.reads.enum.manual', {
                defaultValue: name(o, j),
              })}
            </option>
          ))}
        </select>
      );
    }
    case 'percent':
      return typeof v === 'number' ? <Percent v={v} /> : <Raw v={v} why="undocumented" />;
    case 'duration':
      return typeof v === 'number' ? (
        <span title={`${formatNumber(v)} s`}>{v >= 60 ? duration(v) : `${formatNumber(v)} s`}</span>
      ) : (
        <Raw v={v} why="undocumented" />
      );
    case 'time':
      return typeof v === 'string' ? <TimeBox v={v} /> : <Raw v={v} why="undocumented" />;
    case 'datetime':
      return typeof v === 'string' ? <DateTimeBox v={v} spec={spec} /> : <Raw v={v} why="undocumented" />;
    case 'text':
      return (
        <input
          className="input ro-input"
          value={String(v).replace(/^"(.*)"$/, '$1')}
          readOnly
          tabIndex={-1}
          aria-label={label(k)}
        />
      );
    case 'id':
      return <code className="ro-id">{String(v)}</code>;
    case 'weekdays':
      return Array.isArray(v) ? (
        <span className="ro-chips">
          {(['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const).map((d) => (
            <span key={d} className={`ro-chip${v.includes(d) ? ' is-on' : ''}`}>
              {t(`devices.reads.view.day.${d}`)}
            </span>
          ))}
        </span>
      ) : (
        <Raw v={v} why="undocumented" />
      );
    case 'features':
    case 'alarms':
    case 'flags':
      return isBits(v) ? <BitSet v={v} kind={kind} /> : <Raw v={v} why="undocumented" />;
    case 'number': {
      if (typeof v !== 'number') return <Raw v={v} why="undocumented" />;
      const digits = Number.isInteger(v) ? 0 : Math.min(3, String(v).split('.')[1]?.length ?? 0);
      return `${formatNumber(Number(v.toFixed(3)), digits)}${spec.unit ? ` ${spec.unit}` : ''}`;
    }
    default:
      return <Raw v={v} why="undocumented" />;
  }
}

/** A bit set: enabled functions as switches, alarms as badges, other flags as chips; numeric members raw. */
function BitSet({ v, kind }: { v: Obj & { bits: Obj }; kind: 'features' | 'alarms' | 'flags' }) {
  const { t } = useTranslation();
  const label = useLabel();
  const bits = Object.entries(v.bits);
  const flags = bits.filter((e): e is [string, boolean] => typeof e[1] === 'boolean');
  const other = bits.filter(([, b]) => typeof b !== 'boolean');
  if (kind === 'alarms') {
    const active = flags.filter(([, b]) => b);
    return (
      <span className="ro-chips">
        {active.length ? (
          active.map(([n]) => <AlarmBadge key={n} active label={label(n)} />)
        ) : (
          <AlarmBadge active={false} label={t('devices.reads.view.noAlarms')} />
        )}
      </span>
    );
  }
  return (
    <span className={kind === 'features' ? 'ro-features' : 'ro-chips'}>
      {flags.map(([n, b]) =>
        kind === 'features' ? (
          <span key={n} className="ro-feature">
            <span>{label(n)}</span>
            <Switch on={b} />
          </span>
        ) : (
          <span key={n} className={`ro-chip${b ? ' is-on' : ''}`}>
            {label(n)}
          </span>
        ),
      )}
      {other.map(([n, x]) => (
        <span key={n} className="ro-feature">
          <span>{label(n)}</span>
          <Raw v={x} why="undocumented" />
        </span>
      ))}
    </span>
  );
}

/* ---------- editable controls (device forms) ---------- */

/** Number box that keeps what is typed; reports NaN while the text is not a number. */
export function NumberBox({
  v,
  onChange,
  lim,
  unit,
  label,
}: {
  v: number;
  onChange: (v: number) => void;
  lim?: { min?: number; max?: number; step?: number };
  unit?: string;
  label: string;
}) {
  const [text, setText] = useState(String(v));
  const shown = Number(text) === v || (text === '' && Number.isNaN(v)) ? text : String(v);
  return (
    <span className="ro-edit-number">
      <input
        className="input ro-input ro-num"
        type="number"
        inputMode="decimal"
        value={shown}
        min={lim?.min}
        max={lim?.max}
        step={lim?.step ?? 'any'}
        aria-label={label}
        onChange={(e) => {
          setText(e.target.value);
          onChange(e.target.value.trim() === '' ? NaN : Number(e.target.value));
        }}
      />
      {unit && <span className="muted small">{unit}</span>}
    </span>
  );
}

export function SwitchEdit({
  on,
  onChange,
  label,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={`ro-switch ro-switch-edit${on ? ' is-on' : ''}`}
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
    >
      <span className="ro-switch-track" aria-hidden>
        <span className="ro-switch-thumb" />
      </span>
      {on ? t('devices.reads.view.yes') : t('devices.reads.view.no')}
    </button>
  );
}

export function TimeEdit({
  v,
  onChange,
  label,
  compact = false,
}: {
  v: string;
  onChange: (v: string) => void;
  label: string;
  compact?: boolean;
}) {
  // "HH:MM" in the form; a compact rule window travels as "HHMM" (registry field spec)
  const shown = hhmm(v) ?? v;
  return (
    <input
      className="input ro-input ro-time"
      value={shown}
      maxLength={5}
      placeholder="HH:MM"
      aria-label={label}
      onChange={(e) => {
        const x = e.target.value;
        onChange(compact && /^\d\d:\d\d$/.test(x) ? x.replace(':', '') : x);
      }}
    />
  );
}

/** One editable value: the control matches the field's kind; limits from its edit spec. */
function EditControl({ k, v, edit }: { k: string; v: Json; edit: EditState }): ReactNode {
  const { t } = useTranslation();
  const label = useLabel();
  const spec = specOf(k) ?? {};
  const e = editSpec(k, edit.protocol)!;
  const set = (x: Json) => edit.set(k, x);
  if (Array.isArray(v)) {
    const one = (i: number) => (x: Json) => set(v.map((y, j) => (j === i ? x : y)));
    return (
      <span className="ro-channels">
        {v.map((x, i) => (
          <span key={i}>
            {v.length > 1 && (
              <span className="muted small">{t('devices.reads.view.channel', { n: i + 1 })} </span>
            )}
            <EditControl k={k} v={x} edit={{ ...edit, set: (_key, y) => one(i)(y) }} />
          </span>
        ))}
      </span>
    );
  }
  const lim = { min: e.min ?? spec.min, max: e.max ?? spec.max, step: e.step };
  if (spec.kind === 'bool' && typeof v === 'boolean')
    return <SwitchEdit on={v} onChange={set} label={label(k)} />;
  if (spec.kind === 'enum') {
    const name = (o: string | number, j: number) => String(spec.names?.[j] ?? o);
    return (
      <select
        className="input ro-input"
        value={String(v)}
        aria-label={label(k)}
        onChange={(ev) => {
          const o = spec.options!.find((x) => String(x) === ev.target.value)!;
          set(o);
        }}
      >
        {spec.options!.map((o, j) => (
          <option key={String(o)} value={String(o)}>
            {t(`devices.reads.enum.${name(o, j)}` as 'devices.reads.enum.manual', {
              defaultValue: name(o, j),
            })}
          </option>
        ))}
      </select>
    );
  }
  if (spec.kind === 'percent' && typeof v === 'number')
    return (
      <span className="ro-edit-percent">
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={Number.isFinite(v) ? v : 0}
          aria-label={label(k)}
          onChange={(ev) => set(Number(ev.target.value))}
        />
        <NumberBox v={v} onChange={set} lim={{ min: 0, max: 100, step: 1 }} unit="%" label={label(k)} />
      </span>
    );
  if (spec.kind === 'time' && typeof v === 'string')
    return <TimeEdit v={v} onChange={set} label={label(k)} />;
  if ((spec.kind === 'features' || spec.kind === 'flags') && isBits(v) && e.bits) {
    const bits = v.bits as Record<string, boolean>;
    return (
      <span className="ro-features">
        {e.bits
          .filter((n): n is string => !!n && n in bits)
          .map((n) => (
            <span key={n} className="ro-feature">
              <span>{label(n)}</span>
              <SwitchEdit
                on={!!bits[n]}
                onChange={(on) => set({ ...v, bits: { ...bits, [n]: on } })}
                label={label(n)}
              />
            </span>
          ))}
      </span>
    );
  }
  if (spec.kind === 'group' && isObj(v) && e.members)
    return (
      <span className="ro-group">
        {Object.entries(v).map(([m, x]) => (
          <div key={m} className="ro-row">
            <span className="ro-label">{label(m)}</span>
            <span className="ro-value">
              {m in e.members! && typeof x === 'number' ? (
                <NumberBox
                  v={x}
                  onChange={(y) => set({ ...v, [m]: y })}
                  lim={e.members![m]}
                  unit={specOf(m)?.unit}
                  label={label(m)}
                />
              ) : (
                <Control k={m} v={x} spec={specOf(m)} />
              )}
            </span>
          </div>
        ))}
      </span>
    );
  if (typeof v === 'number')
    return (
      <NumberBox
        v={v}
        onChange={set}
        lim={lim}
        unit={spec.kind === 'duration' ? 's' : spec.unit}
        label={label(k)}
      />
    );
  return <Control k={k} v={v} spec={spec} />;
}

/** The control of one field: editable when the form allows it, read-only otherwise. */
function FieldValue({ k, v }: { k: string; v: Json }) {
  const edit = useEditable(k, v);
  const spec = specOf(k);
  if (edit) {
    const cur = k in edit.draft ? edit.draft[k]! : v;
    return <EditControl k={k} v={cur} edit={edit} />;
  }
  return spec?.kind === 'group' && isObj(v) ? <Group o={v} /> : <Control k={k} v={v} spec={spec} />;
}

function FieldError({ k, v }: { k: string; v: Json }) {
  const { t } = useTranslation();
  const edit = useEditable(k, v);
  if (!edit || !(k in edit.draft)) return null;
  const err = validate(k, edit.draft[k]!, edit.protocol);
  return err ? (
    <span className="ro-error">{t(`devices.edit.invalid.${err}` as 'devices.edit.invalid.min')}</span>
  ) : null;
}

/* ---------- rows, sections and composite cards ---------- */

function Row({ k, v, label: l }: { k: string; v: Json; label?: string }) {
  const label = useLabel();
  const edit = useEditable(k, v);
  const changed = !!edit && k in edit.draft && !same(edit.draft[k], v);
  return (
    <div className={`ro-row${edit ? ' is-editable' : ''}${changed ? ' is-changed' : ''}`}>
      <span className="ro-label">{l ?? label(k)}</span>
      <span className="ro-value">
        <FieldValue k={k} v={v} />
        <FieldError k={k} v={v} />
      </span>
    </div>
  );
}

/** Members of a threshold tuple etc., inline. */
function Group({ o }: { o: Obj }) {
  return (
    <span className="ro-group">
      {Object.entries(o).map(([k, v]) => (
        <Row key={k} k={k} v={v} />
      ))}
    </span>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="ro-card">
      <legend>{title}</legend>
      {children}
    </fieldset>
  );
}

function FromTo({ from, to, fk, tk }: { from: Json; to: Json; fk: string; tk: string }) {
  const { t } = useTranslation();
  return (
    <span className="ro-fromto">
      <span className="muted small">{t('devices.reads.view.from')}</span>
      <Control k={fk} v={from} spec={specOf(fk)} />
      <span className="muted small">{t('devices.reads.view.to')}</span>
      <Control k={tk} v={to} spec={specOf(tk)} />
    </span>
  );
}

/** Switch on/off by illuminance: thresholds, dimming on each side and the evaluation window (editable as one value). */
function LightRuleCard({ r }: { r: Obj }) {
  const { t } = useTranslation();
  const label = useLabel();
  const edit = useEditable('lux_rule', r);
  const cur = (edit?.draft.lux_rule as Obj | undefined) ?? r;
  const e = edit ? editSpec('lux_rule', edit.protocol) : undefined;
  const put = (m: string, x: Json) => edit!.set('lux_rule', { ...cur, [m]: x });
  const num = (m: string, labelText: string, unit?: string) =>
    edit && typeof cur[m] === 'number' ? (
      <NumberBox v={cur[m]} onChange={(x) => put(m, x)} lim={e?.members?.[m]} unit={unit} label={labelText} />
    ) : (
      <Control k={m} v={cur[m] ?? null} spec={specOf(m)} />
    );
  const win = (m: 'window_start' | 'window_end', labelText: string) =>
    edit && typeof cur[m] === 'string' && hhmm(cur[m]) ? (
      <TimeEdit v={cur[m]} onChange={(x) => put(m, x)} label={labelText} compact />
    ) : (
      <Control k={m} v={cur[m] ?? null} spec={specOf(m)} />
    );
  const err = edit ? validate('lux_rule', cur, edit.protocol) : null;
  const changed = !!edit && 'lux_rule' in edit.draft && !same(edit.draft.lux_rule, r);
  const row = (lbl: string, control: ReactNode) => (
    <div className="ro-row">
      <span className="ro-label">{lbl}</span>
      <span className="ro-value">{control}</span>
    </div>
  );
  return (
    <Card title={label('lux_rule')}>
      <div className={`ro-rule${changed ? ' is-changed' : ''}`}>
        <div className="ro-rule-side">
          <h5>{t('devices.reads.view.switchOn')}</h5>
          {row(t('devices.reads.view.whenBelow'), num('on_lux', t('devices.reads.view.whenBelow'), 'lx'))}
          {row(t('devices.reads.view.dimming'), num('on_dimming', t('devices.reads.view.dimming'), '%'))}
        </div>
        <div className="ro-rule-side">
          <h5>{t('devices.reads.view.switchOff')}</h5>
          {row(t('devices.reads.view.whenAbove'), num('off_lux', t('devices.reads.view.whenAbove'), 'lx'))}
          {row(t('devices.reads.view.dimming'), num('off_dimming', t('devices.reads.view.dimming'), '%'))}
        </div>
      </div>
      {row(
        t('devices.reads.view.window'),
        <span className="ro-fromto">
          <span className="muted small">{t('devices.reads.view.from')}</span>
          {win('window_start', t('devices.reads.view.from'))}
          <span className="muted small">{t('devices.reads.view.to')}</span>
          {win('window_end', t('devices.reads.view.to'))}
        </span>,
      )}
      {err && (
        <span className="ro-error">{t(`devices.edit.invalid.${err}` as 'devices.edit.invalid.min')}</span>
      )}
    </Card>
  );
}

/** Light-sensor control: on/off, active window, measured illuminance and the brightness segments. */
function LightSensorCard({ o }: { o: Obj }) {
  const { t } = useTranslation();
  const label = useLabel();
  const segs = [1, 2, 3, 4].filter((n) => `photocell_seg${n}_brightness` in o);
  const one = (k: string) => (k in o ? <Row k={k} v={o[k]!} /> : null);
  return (
    <Card title={t('devices.reads.section.light_sensor')}>
      {one('photocell_enable')}
      {('photocell_enable_time' in o || 'photocell_end_time' in o) && (
        <div className="ro-row">
          <span className="ro-label">{t('devices.reads.view.window')}</span>
          <span className="ro-value">
            <FromTo
              from={o.photocell_enable_time ?? null}
              to={o.photocell_end_time ?? null}
              fk="photocell_enable_time"
              tk="photocell_end_time"
            />
          </span>
        </div>
      )}
      {one('illuminance_lux')}
      {one('photocell_segment')}
      {one('photocell_chip_status')}
      {segs.length > 0 && (
        <table className="ro-table">
          <thead>
            <tr>
              <th>{t('devices.reads.view.segment')}</th>
              <th>{t('devices.reads.view.fromLux')}</th>
              <th>{t('devices.reads.view.toLux')}</th>
              <th>{label('dimming')}</th>
            </tr>
          </thead>
          <tbody>
            {segs.map((n) => (
              <tr key={n}>
                <td>{n}</td>
                {(['lower_lux', 'upper_lux', 'brightness'] as const).map((f) => {
                  const k = `photocell_seg${n}_${f}`;
                  return <td key={f}>{k in o ? <FieldValue k={k} v={o[k]!} /> : '—'}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

const POSITION = [
  'latitude',
  'longitude',
  'gps_status',
  'gps_result',
  'latitude_variation_threshold',
  'longitude_variation_threshold',
  'displacement_times',
];
function PositionCard({ o }: { o: Obj }) {
  const { t } = useTranslation();
  return (
    <Card title={t('devices.reads.section.position')}>
      {typeof o.latitude === 'number' && typeof o.longitude === 'number' && (
        <div className="ro-row">
          <span className="ro-label">{t('devices.reads.view.coordinates')}</span>
          <span className="ro-value ro-coords">
            <span>
              <span className="muted small">{t('devices.reads.field.latitude')}</span> {o.latitude.toFixed(6)}
            </span>
            <span>
              <span className="muted small">{t('devices.reads.field.longitude')}</span>{' '}
              {o.longitude.toFixed(6)}
            </span>
          </span>
        </div>
      )}
      {POSITION.filter((k) => k !== 'latitude' && k !== 'longitude' && k in o).map((k) => (
        <Row key={k} k={k} v={o[k]!} />
      ))}
    </Card>
  );
}

/** Server address `host:port[,rest]`: host and port apart; anything after them is shown raw (no documented meaning). */
function ServerCard({ o }: { o: Obj }) {
  const { t } = useTranslation();
  const label = useLabel();
  return (
    <Card title={t('devices.reads.view.server')}>
      {(['southbound_address', 'southbound_domain'] as const)
        .filter((k) => k in o)
        .map((k) => {
          const v = String(o[k] ?? '');
          const m = /^([^:,]+):(\d{1,5})(?:,(.*))?$/.exec(v);
          return (
            <div key={k} className="ro-row">
              <span className="ro-label">{label(k)}</span>
              <span className="ro-value ro-server">
                {m ? (
                  <>
                    <label>
                      <span className="muted small">{t('devices.reads.view.host')}</span>
                      <input className="input ro-input" value={m[1]} readOnly tabIndex={-1} />
                    </label>
                    <label>
                      <span className="muted small">{t('devices.reads.view.port')}</span>
                      <input className="input ro-input ro-port" value={m[2]} readOnly tabIndex={-1} />
                    </label>
                    {m[3] !== undefined && <Raw v={m[3]} why="undocumented" />}
                  </>
                ) : v ? (
                  <Raw v={v} why="undocumented" />
                ) : (
                  <span className="muted">—</span>
                )}
              </span>
            </div>
          );
        })}
    </Card>
  );
}

/** Editable task row of the form (one dimming task: documented operation type 1). */
type TaskDraft = { task_no: number; time: string; dimming: number; period_min: number };
export const TASK_SLOTS = 10;

/** Task list as a table; empty slots are counted, not listed. Editable for dimming tasks when the form allows it. */
function TasksCard({ tasks, extra }: { tasks: Json[]; extra: Obj }) {
  const { t } = useTranslation();
  const label = useLabel();
  const ctx = useContext(EditCtx);
  const rows = tasks.filter((x): x is Obj => isObj(x) && x.empty !== true);
  const free = tasks.length - rows.length;
  // Only dimming tasks (op type 1) without scenes are writable: the scene mask has no documented meaning.
  const canEdit =
    !!ctx?.writes.has('schedule.write') &&
    rows.every((r) => !('scenes' in r) && (r.op_type === undefined || r.op_type === '1'));
  if (ctx && canEdit) {
    const original: TaskDraft[] = rows.map((r) => ({
      task_no: Number(r.task_no),
      time: String(r.time ?? ''),
      dimming: Number(r.dimming ?? 0),
      period_min: Number(r.period_min ?? 1440),
    }));
    const draft = (ctx.draft.__tasks as TaskDraft[] | undefined) ?? original;
    const put = (next: TaskDraft[]) => ctx.set('__tasks', next as unknown as Json);
    const used = new Set(draft.map((d) => d.task_no));
    const nextNo = Array.from({ length: TASK_SLOTS }, (_, i) => i + 1).find((n) => !used.has(n));
    const edit = (i: number, patch: Partial<TaskDraft>) =>
      put(draft.map((d, j) => (j === i ? { ...d, ...patch } : d)));
    return (
      <Card title={t('devices.reads.field.tasks')}>
        {Object.entries(extra).map(([k, v]) => (
          <Row key={k} k={k} v={v} />
        ))}
        <table className="ro-table">
          <thead>
            <tr>
              <th>{label('task_no')}</th>
              <th>{label('time')}</th>
              <th>{label('dimming')}</th>
              <th>{label('period_min')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {draft.map((d, i) => (
              <tr key={d.task_no}>
                <td>{d.task_no}</td>
                <td>
                  <TimeEdit v={d.time} onChange={(x) => edit(i, { time: x })} label={label('time')} />
                </td>
                <td>
                  <NumberBox
                    v={d.dimming}
                    onChange={(x) => edit(i, { dimming: x })}
                    lim={{ min: 0, max: 100, step: 1 }}
                    unit="%"
                    label={label('dimming')}
                  />
                </td>
                <td>
                  <NumberBox
                    v={d.period_min}
                    onChange={(x) => edit(i, { period_min: x })}
                    lim={{ min: 1, max: 65535, step: 1 }}
                    unit="min"
                    label={label('period_min')}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={t('devices.edit.removeTask', { n: d.task_no })}
                    onClick={() => put(draft.filter((_, j) => j !== i))}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {nextNo !== undefined && (
          <button
            type="button"
            className="btn"
            onClick={() =>
              put([...draft, { task_no: nextNo, time: '18:00', dimming: 100, period_min: 1440 }])
            }
          >
            <Plus size={14} /> {t('devices.edit.addTask')}
          </button>
        )}
      </Card>
    );
  }
  const cols = [...new Set(rows.flatMap((r) => Object.keys(r)))].filter((c) => c !== 'empty');
  return (
    <Card title={t('devices.reads.field.tasks')}>
      {Object.entries(extra).map(([k, v]) => (
        <Row key={k} k={k} v={v} />
      ))}
      {rows.length === 0 ? (
        <p className="muted small">{t('devices.reads.view.noTasks')}</p>
      ) : (
        <table className="ro-table">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c}>{label(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {cols.map((c) => (
                  <td key={c}>{c in r ? <Control k={c} v={r[c]!} spec={specOf(c)} /> : '—'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {free > 0 && <p className="muted small">{t('devices.reads.view.freeSlots', { n: free })}</p>}
    </Card>
  );
}

/** Sunrise/sunset offsets: one row per moment. */
function OffsetsCard({ d }: { d: Obj }) {
  const label = useLabel();
  return (
    <Card title={label('deviations')}>
      <table className="ro-table">
        <thead>
          <tr>
            <th />
            <th>{label('offset_min')}</th>
            <th>{label('dimming')}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(d).map(([k, v]) => (
            <tr key={k}>
              <td>{label(k)}</td>
              <td>
                {isObj(v) ? (
                  <Control k="offset_min" v={v.offset_min ?? null} spec={specOf('offset_min')} />
                ) : (
                  '—'
                )}
              </td>
              <td>
                {isObj(v) ? <Control k="dimming" v={v.dimming ?? null} spec={specOf('dimming')} /> : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/** Report list: the parameters the device sends, by name, in order. Editable (add, remove, reorder) when allowed. */
function ParamListCard({ keys }: { keys: Json[] }) {
  const { t } = useTranslation();
  const label = useLabel();
  const ctx = useContext(EditCtx);
  const [adding, setAdding] = useState('');
  const original = keys.map(String);
  if (ctx?.writes.has('report_config.write')) {
    const list = (ctx.draft.__keys as string[] | undefined) ?? original;
    const put = (next: string[]) => ctx.set('__keys', next);
    const move = (i: number, d: number) => {
      const next = [...list];
      [next[i], next[i + d]] = [next[i + d]!, next[i]!];
      put(next);
    };
    const options = reportable().filter((k) => !list.includes(k));
    const over = list.length > REPORT_LIST_MAX;
    return (
      <Card title={t('devices.reads.field.keys')}>
        <p className={`small ${over || !list.length ? 'ro-error' : 'muted'}`}>
          {t('devices.edit.listCount', { n: list.length, max: REPORT_LIST_MAX })}
        </p>
        <ol className="ro-list ro-list-edit">
          {list.map((k, i) => (
            <li key={k}>
              <span>
                {label(k)}
                {specOf(k)?.unit && <span className="muted small"> ({specOf(k)!.unit})</span>}
              </span>
              <span className="ro-list-actions">
                <button
                  type="button"
                  className="icon-btn"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                  aria-label={t('devices.edit.up')}
                >
                  <ArrowUp size={13} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  disabled={i === list.length - 1}
                  onClick={() => move(i, 1)}
                  aria-label={t('devices.edit.down')}
                >
                  <ArrowDown size={13} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => put(list.filter((x) => x !== k))}
                  aria-label={t('devices.edit.remove', { name: label(k) })}
                >
                  <Trash2 size={13} />
                </button>
              </span>
            </li>
          ))}
        </ol>
        <span className="toolbar">
          <select
            className="input ro-input"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            aria-label={t('devices.edit.addParam')}
          >
            <option value="">{t('devices.edit.chooseParam')}</option>
            {options.map((k) => (
              <option key={k} value={k}>
                {label(k)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn"
            disabled={!adding || list.length >= REPORT_LIST_MAX}
            onClick={() => {
              put([...list, adding]);
              setAdding('');
            }}
          >
            <Plus size={14} /> {t('devices.edit.addParam')}
          </button>
        </span>
      </Card>
    );
  }
  return (
    <Card title={t('devices.reads.field.keys')}>
      <ol className="ro-list">
        {keys.map((k, i) => {
          const s = specOf(String(k));
          return (
            <li key={i}>
              {label(String(k))}
              {s?.unit && <span className="muted small"> ({s.unit})</span>}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

/** Catalog metrics of a status answer, labelled and formatted like the telemetry grid. */
function MetricsCard({ m }: { m: Record<string, number> }) {
  const { t } = useTranslation();
  const label = useMetricLabel();
  return (
    <Card title={t('devices.reads.view.metrics')}>
      {Object.entries(m).map(([k, v]) => (
        <div key={k} className="ro-row">
          <span className="ro-label">{label(k)}</span>
          <span className="ro-value">{k.endsWith('_pct') ? <Percent v={v} /> : formatMetric(k, v)}</span>
        </div>
      ))}
    </Card>
  );
}

/** The fields of an answer (config/schedule wrappers flattened; metrics, vendor values and status codes left out). */
export function bodyOf(result: Record<string, unknown>): Obj {
  const { metrics: _m, vendor: _v, raw: _r, answer: _a, answer_name: _n, ...rest } = result as Obj;
  const body: Obj = {};
  for (const [k, v] of Object.entries(rest))
    if ((k === 'config' || k === 'schedule') && isObj(v)) Object.assign(body, v);
    else body[k] = v;
  return body;
}

/** A value with its read-only control (change lists, read-back confirmation). */
export function ValueView({ k, v }: { k: string; v: Json }) {
  const spec = specOf(k);
  return spec?.kind === 'group' && isObj(v) ? <Group o={v} /> : <Control k={k} v={v} spec={spec} />;
}

export { useLabel as useFieldLabel };

export function ReadResult({
  result,
  edit = null,
}: {
  result: Record<string, unknown>;
  edit?: EditState | null;
}) {
  const { t } = useTranslation();
  const r = result as Obj;
  const { metrics, vendor, raw: _raw, answer: _answer, answer_name, ...rest } = r;
  const body: Obj = {};
  for (const [k, v] of Object.entries(rest))
    if ((k === 'config' || k === 'schedule') && isObj(v)) Object.assign(body, v);
    else body[k] = v;

  const cards: ReactNode[] = [];
  const take = (keys: string[]) => {
    const o: Obj = {};
    for (const k of keys)
      if (k in body) {
        o[k] = body[k]!;
        delete body[k];
      }
    return o;
  };
  if (isObj(metrics)) cards.push(<MetricsCard key="metrics" m={metrics as Record<string, number>} />);
  if (isObj(body.lux_rule)) cards.push(<LightRuleCard key="rule" r={take(['lux_rule']).lux_rule as Obj} />);
  const sensorKeys = Object.keys(body).filter((k) => k.startsWith('photocell_') || k === 'illuminance_lux');
  if (sensorKeys.some((k) => k.startsWith('photocell_')))
    cards.push(<LightSensorCard key="sensor" o={take(sensorKeys)} />);
  if ('latitude' in body || 'gps_result' in body) cards.push(<PositionCard key="pos" o={take(POSITION)} />);
  if ('southbound_address' in body || 'southbound_domain' in body)
    cards.push(<ServerCard key="server" o={take(['southbound_address', 'southbound_domain'])} />);
  if (Array.isArray(body.tasks)) {
    const tasks = take(['tasks']).tasks as Json[];
    cards.push(<TasksCard key="tasks" tasks={tasks} extra={take(LIST_CONTEXT)} />);
  }
  if (isObj(body.deviations))
    cards.push(<OffsetsCard key="offsets" d={take(['deviations', ...LIST_CONTEXT]).deviations as Obj} />);
  if (Array.isArray(body.keys)) cards.push(<ParamListCard key="list" keys={take(['keys']).keys as Json[]} />);

  // Remaining fields grouped by section in the registry's order; fields without semantics last, marked.
  const bySection = new Map<string, [string, Json][]>();
  for (const [k, v] of Object.entries(body)) {
    const s = specOf(k);
    const sec = s ? (s.section ?? 'other') : 'undocumented';
    bySection.set(sec, [...(bySection.get(sec) ?? []), [k, v]]);
  }
  return (
    <EditCtx.Provider value={edit}>
      <div className="read-view">
        {answer_name === 'PARTIAL' && <p className="muted small">{t('devices.reads.view.partial')}</p>}
        <div className="ro-cards">
          {cards}
          {[...SECTIONS, 'undocumented']
            .filter((s) => bySection.has(s))
            .map((s) => (
              <Card key={s} title={t(`devices.reads.section.${s}` as 'devices.reads.section.other')}>
                {bySection.get(s)!.map(([k, v]) => (
                  <Row key={k} k={k} v={v} />
                ))}
              </Card>
            ))}
        </div>
        {isObj(vendor) && (
          <details className="read-raw">
            <summary>{t('devices.reads.view.vendor')}</summary>
            <div className="ro-card">
              {Object.entries(vendor).map(([k, v]) => (
                <div key={k} className="ro-row">
                  <code className="ro-label">{k}</code>
                  <span className="ro-value">
                    <Raw v={v} why="undocumented" />
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
        <details className="read-raw">
          <summary>{t('devices.reads.view.raw')}</summary>
          <pre className="mono small read-result">{JSON.stringify(result, null, 2)}</pre>
        </details>
      </div>
    </EditCtx.Provider>
  );
}
